import type { CoverageSubmissionPayload } from "@itaas/insurance-contract";
import {
  encodeSubmissionImport,
  insurerCodeToOrgId,
  submissionImportUrl,
} from "@itaas/insurance-contract";
import { loadStoredSession } from "@itaas/core/auth/auth.storage";
import { getConsultationById, getPrescriptionById } from "@/core/clinical/clinical-store";
import { IAM_TENANT_SLUG } from "@/core/config";
import { getTenantDisplayName } from "@/core/tenant-display";
import { getPatientById } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import {
  getCoverageCase,
  markCoverageCaseSubmittedToHub,
} from "./coverage-case-store";
import { postSubmissionImportToHubApi } from "./hub-api-client";
import { getInsurerById } from "./insurer-store";
import { getActivePartnershipForInsurer, defaultInsuranceHubUrl } from "./partnership-store";

export function buildHubSubmissionForCase(
  caseId: string,
): { token: string; importUrl: string; payload: CoverageSubmissionPayload } | null {
  const coverageCase = getCoverageCase(caseId);
  if (!coverageCase) return null;

  const insurer = getInsurerById(coverageCase.insurerId);
  const patient = getPatientById(coverageCase.patientId);
  const partnership = getActivePartnershipForInsurer(coverageCase.insurerId);
  if (!insurer || !patient || !partnership) return null;

  const session = loadStoredSession();
  const tenantSlug = session?.tenant?.slug ?? IAM_TENANT_SLUG;
  const tenantName = getTenantDisplayName();

  let clinical = null;
  let prescriptionLines = undefined;

  if (coverageCase.prescriptionId) {
    const rx = getPrescriptionById(coverageCase.prescriptionId);
    if (rx) {
      prescriptionLines = rx.lines.map((line) => ({
        label: line.productLabel,
        quantity: line.quantity,
        instructions: line.instructions,
      }));
      if (rx.consultationId) {
        const consultation = getConsultationById(rx.consultationId);
        if (consultation) {
          clinical = {
            consultationId: consultation.id,
            motif: consultation.motif,
            diagnostic: consultation.diagnostic,
            acts: consultation.acts,
            closedAt: consultation.closedAt,
          };
        }
      }
    }
  }

  const payload: CoverageSubmissionPayload = {
    submissionId: `sub-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    partnershipId: partnership.id,
    hospitalTenantSlug: tenantSlug,
    hospitalName: tenantName,
    insurerOrgId: insurerCodeToOrgId(insurer.code),
    patientId: patient.id,
    patientName: patientFullName(patient),
    memberNumber: coverageCase.memberNumber,
    policyNumber: coverageCase.policyNumber,
    kind: coverageCase.kind,
    label: coverageCase.label,
    amount: coverageCase.amount,
    clinical,
    prescriptionLines,
    submittedAt: new Date().toISOString(),
  };

  const token = encodeSubmissionImport(payload);
  const hubUrl = defaultInsuranceHubUrl();
  return { token, importUrl: submissionImportUrl(token, hubUrl), payload };
}

export async function submitCoverageCaseToHub(caseId: string): Promise<{
  importUrl: string;
  submissionId: string;
  via: "api" | "url";
  error?: string;
} | null> {
  const built = buildHubSubmissionForCase(caseId);
  if (!built) return null;

  const apiResult = await postSubmissionImportToHubApi(built.token);
  if (apiResult.ok && apiResult.submissionId) {
    markCoverageCaseSubmittedToHub(caseId, apiResult.submissionId);
    return {
      importUrl: built.importUrl,
      submissionId: apiResult.submissionId,
      via: "api",
    };
  }

  markCoverageCaseSubmittedToHub(caseId, built.payload.submissionId);
  return {
    importUrl: built.importUrl,
    submissionId: built.payload.submissionId,
    via: "url",
    error: apiResult.error,
  };
}
